import { queryClient } from '@/infra/http'
import { userContentDetailsService } from '@/data/services/content/user-content-details-service'
import { userEnrollmentContentInfoService } from '@/data/services/content/user-enrollment-content-info-service'
import { productContentService } from '@/data/services/product/product-content-service'
import { PRODUCT_AVAILABLE_INFO_QUERY_KEY } from '@/data/services/product/product-content-service-types'
import { isDependent } from '@/presentation/hooks/content/use-is-dependent'

/**
 * Data loader da rota de detalhes do conteúdo (titulares e dependentes).
 *
 * Executa 3 queries em paralelo antes de renderizar a página:
 *
 * 1. `userContentDetails` — dados do produto (nome, formato, instituição, etc.)
 * 2. `userEnrollmentContentInfo` — elegibilidade e restrições de matrícula do usuário
 * 3. `productAvailableInfo` — cobertura de plano e planos disponíveis para o produto
 *
 * Os estados disponíveis (usado por `use-enrollment-wizard-flags` e `EnrollmentUnitSelect`)
 * são buscados diretamente por esses componentes ao montar. O wizard exibe um Skeleton
 * enquanto essa query carrega, evitando blink de conteúdo.
 */
export async function loadContentDetails(contentId: number) {
  const dependentContext = isDependent()

  const [userContentDetails, userEnrollmentContentInfo, productAvailableInfo] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: ['user-content-details', contentId],
      queryFn: () => userContentDetailsService.fetchUserContentDetails(contentId),
      staleTime: 1000
    }),
    queryClient.fetchQuery({
      queryKey: ['user-enrollment-content-info', contentId],
      queryFn: () => userEnrollmentContentInfoService.fetchUserEnrollmentContentInfo(contentId),
      staleTime: 1000
    }),
    queryClient.fetchQuery({
      queryKey: [PRODUCT_AVAILABLE_INFO_QUERY_KEY, contentId],
      queryFn: () =>
        productContentService.fetchAvailableInfo({
          productId: contentId,
          isDependentContext: dependentContext
        }),
      staleTime: 0
    })
  ])

  return {
    userContentDetails,
    userEnrollmentContentInfo,
    productAvailableInfo
  }
}
